import { atom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import type { ChatMessage, ChatWidget } from '../types/coach';
import { dateAwareStorage } from './storage-utils';

// Thread-specific state interfaces
export interface ThreadState {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  streamingMessage: StreamingMessage | null;
  scrollPosition: number;
}

export interface StreamingMessage {
  id: string;
  role: 'assistant';
  widgets: ChatWidget[];
  timestamp: Date;
  thinking?: string;
}

// Default thread state
const defaultThreadState: ThreadState = {
  messages: [],
  isLoading: false,
  error: null,
  streamingMessage: null,
  scrollPosition: 0,
};

// Storage key generator for thread-specific data
const getThreadStorageKey = (threadId: string) => `treel_thread_${threadId}`;

// Atom family for thread-specific state with localStorage persistence
export const threadStateFamily = atomFamily((threadId: string) =>
  atomWithStorage<ThreadState>(getThreadStorageKey(threadId), defaultThreadState, dateAwareStorage)
);

// Derived atoms for specific thread state properties
export const threadMessagesFamily = atomFamily((threadId: string) =>
  atom(
    (get) => get(threadStateFamily(threadId)).messages,
    (get, set, messages: ChatMessage[]) => {
      const currentState = get(threadStateFamily(threadId));
      set(threadStateFamily(threadId), {
        ...currentState,
        messages,
      });
    }
  )
);

export const threadLoadingFamily = atomFamily((threadId: string) =>
  atom(
    (get) => get(threadStateFamily(threadId)).isLoading,
    (get, set, isLoading: boolean) => {
      const currentState = get(threadStateFamily(threadId));
      set(threadStateFamily(threadId), {
        ...currentState,
        isLoading,
      });
    }
  )
);

export const threadErrorFamily = atomFamily((threadId: string) =>
  atom(
    (get) => get(threadStateFamily(threadId)).error,
    (get, set, error: string | null) => {
      const currentState = get(threadStateFamily(threadId));
      set(threadStateFamily(threadId), {
        ...currentState,
        error,
      });
    }
  )
);

export const threadStreamingMessageFamily = atomFamily((threadId: string) =>
  atom(
    (get) => get(threadStateFamily(threadId)).streamingMessage,
    (get, set, streamingMessage: StreamingMessage | null) => {
      const currentState = get(threadStateFamily(threadId));
      set(threadStateFamily(threadId), {
        ...currentState,
        streamingMessage,
      });
    }
  )
);

export const threadScrollPositionFamily = atomFamily((threadId: string) =>
  atom(
    (get) => get(threadStateFamily(threadId)).scrollPosition,
    (get, set, scrollPosition: number) => {
      const currentState = get(threadStateFamily(threadId));
      set(threadStateFamily(threadId), {
        ...currentState,
        scrollPosition,
      });
    }
  )
);

// Action atoms for thread operations
export const addMessageToThreadFamily = atomFamily((threadId: string) =>
  atom(
    null,
    (get, set, message: ChatMessage) => {
      const currentMessages = get(threadMessagesFamily(threadId));
      const updatedMessages = [...currentMessages, message];
      set(threadMessagesFamily(threadId), updatedMessages);
      
      // Update thread metadata
      const metadata = get(threadMetadataAtom);
      const firstUserMessage = updatedMessages.find(m => m.role === 'user');
      const title = firstUserMessage?.widgets?.[0]?.type === 'text'
        ? firstUserMessage.widgets[0].text.slice(0, 50) + 
          (firstUserMessage.widgets[0].text.length > 50 ? '...' : '')
        : `Thread ${threadId.slice(-8)}`;
      
      set(threadMetadataAtom, {
        ...metadata,
        [threadId]: {
          id: threadId,
          title,
          messageCount: updatedMessages.length,
          lastActivity: new Date()
        }
      });
    }
  )
);

export const updateLastMessageInThreadFamily = atomFamily((threadId: string) =>
  atom(
    null,
    (get, set, updatedMessage: Partial<ChatMessage>) => {
      const currentMessages = get(threadMessagesFamily(threadId));
      if (currentMessages.length === 0) {return;}

      const lastIndex = currentMessages.length - 1;
      const updatedMessages = [...currentMessages];
      updatedMessages[lastIndex] = {
        ...updatedMessages[lastIndex],
        ...updatedMessage,
      };
      
      set(threadMessagesFamily(threadId), updatedMessages);
    }
  )
);

export const clearThreadStateFamily = atomFamily((threadId: string) =>
  atom(
    null,
    (_get, set) => {
      set(threadStateFamily(threadId), defaultThreadState);
    }
  )
);

// Thread metadata for sidebar display
export interface ThreadMetadata {
  id: string;
  title: string;
  messageCount: number;
  lastActivity: Date;
}

// Global thread management atoms with localStorage persistence
export const activeThreadIdAtom = atomWithStorage<string | null>('treel_active_thread_id', null);

export const threadListAtom = atomWithStorage<string[]>('treel_thread_list', []);

// Thread metadata storage for sidebar (avoids hooks in loops)
export const threadMetadataAtom = atomWithStorage<Record<string, ThreadMetadata>>('treel_thread_metadata', {}, dateAwareStorage);

export const createNewThreadAtom = atom(
  null,
  (get, set) => {
    const timestamp = Date.now();
    const newThreadId = `thread_${timestamp}`;
    
    // Initialize the new thread state (will be automatically persisted)
    set(threadStateFamily(newThreadId), defaultThreadState);
    
    // Add to thread list (will be automatically persisted)
    const currentThreads = get(threadListAtom);
    set(threadListAtom, [...currentThreads, newThreadId]);
    
    // Initialize thread metadata
    const metadata = get(threadMetadataAtom);
    set(threadMetadataAtom, {
      ...metadata,
      [newThreadId]: {
        id: newThreadId,
        title: `Thread ${newThreadId.slice(-8)}`,
        messageCount: 0,
        lastActivity: new Date()
      }
    });
    
    // Set as active thread (will be automatically persisted)
    set(activeThreadIdAtom, newThreadId);
    
    return newThreadId;
  }
);

export const removeThreadAtom = atom(
  null,
  (get, set, threadId: string) => {
    // Remove from thread list (will be automatically persisted)
    const currentThreads = get(threadListAtom);
    const remainingThreads = currentThreads.filter(id => id !== threadId);
    set(threadListAtom, remainingThreads);
    
    // Clear thread state (reset to default and let localStorage handle it)
    set(threadStateFamily(threadId), defaultThreadState);
    
    // Remove thread metadata
    const metadata = get(threadMetadataAtom);
    const { [threadId]: _removed, ...remainingMetadata } = metadata;
    set(threadMetadataAtom, remainingMetadata);
    
    // If this was the active thread, set active to null or first available
    const activeThreadId = get(activeThreadIdAtom);
    if (activeThreadId === threadId) {
      set(activeThreadIdAtom, remainingThreads.length > 0 ? remainingThreads[0] : null);
    }
  }
);

// Computed atoms for convenience
export const activeThreadStateAtom = atom((_get) => {
  const activeThreadId = _get(activeThreadIdAtom);
  return activeThreadId ? _get(threadStateFamily(activeThreadId)) : null;
});

export const activeThreadMessagesAtom = atom((_get) => {
  const activeThreadId = _get(activeThreadIdAtom);
  return activeThreadId ? _get(threadMessagesFamily(activeThreadId)) : [];
});

export const activeThreadLoadingAtom = atom((_get) => {
  const activeThreadId = _get(activeThreadIdAtom);
  return activeThreadId ? _get(threadLoadingFamily(activeThreadId)) : false;
});

export const activeThreadErrorAtom = atom((_get) => {
  const activeThreadId = _get(activeThreadIdAtom);
  return activeThreadId ? _get(threadErrorFamily(activeThreadId)) : null;
});

export const activeThreadStreamingMessageAtom = atom((_get) => {
  const activeThreadId = _get(activeThreadIdAtom);
  return activeThreadId ? _get(threadStreamingMessageFamily(activeThreadId)) : null;
});

export const activeThreadScrollPositionAtom = atom((_get) => {
  const activeThreadId = _get(activeThreadIdAtom);
  return activeThreadId ? _get(threadScrollPositionFamily(activeThreadId)) : 0;
});